import { captureMessage } from '@sentry/react';
import { StatusCodes } from 'api/types';
import { get } from 'lodash';

const STATUS_CODES_KEY_TO_I18N_NAMESPACE: Record<string, string> = {
  task: 'tasks',
};

function getI18nNamespaceForResource(nameForStatusCode: string | undefined) {
  if (nameForStatusCode == null) {
    return '';
  }
  return STATUS_CODES_KEY_TO_I18N_NAMESPACE[nameForStatusCode] ?? nameForStatusCode;
}

// A missing entry is only a genuine config/translation gap when the resource's
// status map is actually loaded. When the whole map is absent the status
// endpoint hasn't loaded (or failed) -- a data-availability issue surfaced by
// useStatusQuery -- so we must not report it as a missing translation.
function hasStatusMap(resourceStatusCodes: unknown): boolean {
  return resourceStatusCodes != null && typeof resourceStatusCodes === 'object' && Object.keys(resourceStatusCodes).length > 0;
}

// Resources backed by a string enum (B2C) publish their statuses as a flat list
// of names instead of a code -> name map, so the value is already the name.
export function resolveStatusName(resourceStatusCodes: unknown, status: number | string): string | undefined {
  if (Array.isArray(resourceStatusCodes)) {
    return resourceStatusCodes.includes(status) ? String(status) : undefined;
  }
  return get(resourceStatusCodes, status);
}

export function getI18nKeyForStatus(statusCodes: any, ressourceKey: string, status: number | string, text: string | null = 'label'): string {
  const namespace = ressourceKey?.replace('-', '_');
  const nameForStatusCode = namespace?.split(':')[0];
  const resourceStatusCodes = get(statusCodes, nameForStatusCode, {});
  const i18nNs = getI18nNamespaceForResource(nameForStatusCode);
  const statusName = resolveStatusName(resourceStatusCodes, status);

  if (statusName) {
    return i18nNs + ':status.' + statusName + '.' + text;
  } else {
    if (hasStatusMap(resourceStatusCodes)) {
      captureMessage("can't find translation for status code " + status + ' for resource ' + ressourceKey, {
        level: 'warning',
      });
    }
    return i18nNs + ':status.' + status;
  }
}

// B2C namespaces keep labels flat under `status.<name>`; every other resource
// nests them under `.label`. i18next resolves the first key that exists.
export function getI18nKeysForStatus(statusCodes: any, ressourceKey: string, status: number | string): string[] {
  const key = getI18nKeyForStatus(statusCodes, ressourceKey, status);
  return key.endsWith('.label') ? [key, key.slice(0, -'.label'.length)] : [key];
}

export function getI18nKeyForStatusReason(statusCodes: StatusCodes, ressourceKey: string, status: number | string, statusReason: number): string {
  const namespace = ressourceKey?.replace('-', '_');
  const nameForStatusCode = namespace?.split(':')[0];
  const resourceStatusCodes = get(statusCodes, nameForStatusCode, {} as Record<string, any>);
  const resourceStatusReasonCodes = get(statusCodes, nameForStatusCode + '_reasons', {} as Record<string, any>);
  const i18nNs = getI18nNamespaceForResource(nameForStatusCode);
  const statusName = resolveStatusName(resourceStatusCodes, status);

  if (statusName && resourceStatusReasonCodes?.[status]?.[statusReason]) {
    return i18nNs + ':status.' + statusName + '.' + resourceStatusReasonCodes?.[status]?.[statusReason] + '.label';
  } else {
    if (hasStatusMap(resourceStatusCodes) && hasStatusMap(resourceStatusReasonCodes)) {
      captureMessage("can't find translation for status reason code " + statusReason + ' of status ' + status + ' for resource ' + ressourceKey, {
        level: 'warning',
      });
    }
    return i18nNs + ':status.' + status + '.' + statusReason;
  }
}
